<script setup lang="ts">
import { WARP_STYLES } from '@/text/warp'
// テキストのスタイル編集 (オプションバーとプロパティで共用)。
// layer があればそのレイヤーを編集、なければツールの既定スタイルを編集する。
import { computed } from 'vue'
import { useDocumentStore } from '@/stores/documentStore'
import { useUiStore } from '@/stores/uiStore'
import { useI18n, type I18nKey } from '@/i18n'
import { fontList, canQueryLocalFonts, loadLocalFonts, localFontsLoaded, isFontAvailable, addFontFile } from '@/text/fonts'
import { pickFiles } from '@/io/download'
import { useToast } from '@/composables/useToast'
import type { TextLayer, TextStyle, WarpStyle } from '@/types/document'

const props = defineProps<{ layer?: TextLayer | null; compact?: boolean }>()
const docStore = useDocumentStore()
const ui = useUiStore()
const { t } = useI18n()
const toast = useToast()
const style = computed<TextStyle>(() => props.layer?.style ?? ui.textStyle)
let inTx = false
function set(p: Partial<TextStyle>, commit = true) {
  ui.textStyle = { ...ui.textStyle, ...p }
  if (props.layer) {
    if (!commit && !inTx) {
      docStore.beginTransaction('history.editText')
      inTx = true
    }
    docStore.setText(props.layer.id, { style: p })
    if (commit && inTx) {
      docStore.commitTransaction()
      inTx = false
    }
  }
}
let warpTx = false
function setWarp(p: { style?: string; bend?: number; hDist?: number; vDist?: number }, commit = true) {
  if (!props.layer) return
  if (p.style === 'none') {
    docStore.setText(props.layer.id, { warp: null })
    return
  }
  const cur = props.layer.warp ?? { style: 'arc' as const, bend: 50, hDist: 0, vDist: 0 }
  const next = { ...cur, ...(p.style ? { style: p.style as WarpStyle } : {}), ...(p.bend !== undefined ? { bend: p.bend } : {}), ...(p.hDist !== undefined ? { hDist: p.hDist } : {}), ...(p.vDist !== undefined ? { vDist: p.vDist } : {}) }
  if (!commit && !warpTx) {
    docStore.beginTransaction('history.editText')
    warpTx = true
  }
  docStore.setText(props.layer.id, { warp: next })
  if (commit && warpTx) {
    docStore.commitTransaction()
    warpTx = false
  }
}
async function loadFonts() {
  try {
    await loadLocalFonts()
  } catch {
    /* 拒否 */
  }
}
/** フォントファイルを読み込む。保存はしないので、再読み込みすると消える */
async function loadFontFile() {
  const [file] = await pickFiles('.ttf,.otf,.ttc,.woff,.woff2,font/*', false)
  if (!file) return
  try {
    const family = await addFontFile(await file.arrayBuffer(), file.name)
    set({ fontFamily: family })
    toast.success(t('text.fontFileLoaded', { name: family }))
  } catch (e) {
    toast.error(e instanceof Error ? e.message : String(e))
  }
}
</script>

<template>
  <div class="tc" :class="{ compact }">
    <!-- 一覧から選ぶだけでなく、PC に入っている書体名を直接打てるようにする
         (Local Font Access API の無い Firefox / Safari では一覧に出てこないため) -->
    <input
      :value="style.fontFamily"
      class="font"
      list="bpe-font-list"
      :style="{ fontFamily: style.fontFamily }"
      :class="{ missing: !isFontAvailable(style.fontFamily) }"
      :title="isFontAvailable(style.fontFamily) ? style.fontFamily : `${style.fontFamily} — ${t('text.fontMissing')}`"
      spellcheck="false"
      @change="set({ fontFamily: ($event.target as HTMLInputElement).value.trim() || 'sans-serif' })"
    />
    <datalist id="bpe-font-list">
      <option v-for="f in fontList" :key="f" :value="f">{{ isFontAvailable(f) ? '' : t('text.fontMissing') }}</option>
    </datalist>
    <button v-if="canQueryLocalFonts() && !localFontsLoaded" class="ghost" :title="t('text.loadLocalFonts')" @click="loadFonts">…</button>
    <button class="ghost" :title="t('text.loadFontFile')" @click="loadFontFile">+</button>
    <label class="row"><span class="muted">{{ t('text.size') }}</span><input type="number" class="num" min="1" max="5000" :value="style.fontSize" @change="set({ fontSize: Math.max(1, Number(($event.target as HTMLInputElement).value) || 1) })" /></label>
    <button class="tog" :class="{ on: style.fontWeight >= 600 }" :title="t('text.bold')" @click="set({ fontWeight: style.fontWeight >= 600 ? 400 : 700 })"><b>B</b></button>
    <button class="tog" :class="{ on: style.italic }" :title="t('text.italic')" @click="set({ italic: !style.italic })"><i>I</i></button>
    <input type="color" :value="style.hex" :title="t('text.color')" @input="set({ hex: ($event.target as HTMLInputElement).value }, false)" @change="set({ hex: ($event.target as HTMLInputElement).value })" />
    <span class="group">
      <button v-for="a in (['left', 'center', 'right'] as const)" :key="a" class="tog" :class="{ on: style.align === a }" @click="set({ align: a })">{{ a === 'left' ? '⇤' : a === 'center' ? '↔' : '⇥' }}</button>
    </span>
    <button class="tog" :class="{ on: style.orientation === 'vertical' }" :title="t('text.vertical')" @click="set({ orientation: style.orientation === 'vertical' ? 'horizontal' : 'vertical' })">縦</button>
    <template v-if="!compact">
      <label class="row"><span class="muted">{{ t('text.lineHeight') }}</span><input type="number" class="num" min="0.1" max="10" step="0.05" :value="style.lineHeight" @change="set({ lineHeight: Number(($event.target as HTMLInputElement).value) || 1.2 })" /></label>
      <label class="row"><span class="muted">{{ t('text.letterSpacing') }}</span><input type="number" class="num" min="-100" max="500" :value="style.letterSpacing" @change="set({ letterSpacing: Number(($event.target as HTMLInputElement).value) || 0 })" /></label>
      <template v-if="layer">
        <label class="row"><span class="muted">{{ t('text.warp') }}</span>
          <select class="warp-style" :value="layer.warp?.style ?? 'none'" @change="setWarp({ style: ($event.target as HTMLSelectElement).value })">
            <option value="none">{{ t('text.warp.none') }}</option>
            <option v-for="s in WARP_STYLES" :key="s" :value="s">{{ t(`text.warp.${s}` as I18nKey) }}</option>
          </select>
        </label>
        <template v-if="layer.warp">
          <label class="row"><span class="muted">{{ t('text.warp.bend') }}</span><input type="range" min="-100" max="100" :value="layer.warp.bend" @input="setWarp({ bend: Number(($event.target as HTMLInputElement).value) }, false)" @change="setWarp({ bend: Number(($event.target as HTMLInputElement).value) })" /><span class="mono">{{ layer.warp.bend }}%</span></label>
          <label class="row"><span class="muted">{{ t('text.warp.h') }}</span><input type="range" min="-100" max="100" :value="layer.warp.hDist" @input="setWarp({ hDist: Number(($event.target as HTMLInputElement).value) }, false)" @change="setWarp({ hDist: Number(($event.target as HTMLInputElement).value) })" /><span class="mono">{{ layer.warp.hDist }}%</span></label>
          <label class="row"><span class="muted">{{ t('text.warp.v') }}</span><input type="range" min="-100" max="100" :value="layer.warp.vDist" @input="setWarp({ vDist: Number(($event.target as HTMLInputElement).value) }, false)" @change="setWarp({ vDist: Number(($event.target as HTMLInputElement).value) })" /><span class="mono">{{ layer.warp.vDist }}%</span></label>
        </template>
      </template>
      <label v-if="layer" class="row"><span class="muted">{{ t('text.boxWidth') }}</span><input type="number" class="num" min="0" :value="layer.boxWidth ?? 0" @change="docStore.setText(layer!.id, { boxWidth: Number(($event.target as HTMLInputElement).value) || 0 })" /></label>
    </template>
  </div>
</template>

<style scoped>
.tc {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.tc.compact {
  flex-wrap: nowrap;
}
.font {
  width: 150px;
}
/* この PC に無い書体を指定しているときは、代替で描かれていることが分かるようにする */
.font.missing {
  border-color: var(--warn, #c8892a);
}
.num {
  width: 60px;
}
.tog {
  width: 26px;
  height: 24px;
  padding: 0;
  background: transparent;
  border-color: transparent;
}
.tog.on {
  background: var(--bg-3);
  border-color: var(--line-strong);
  color: var(--accent-hi);
}
.group {
  display: inline-flex;
}
</style>
